import { TruckSearchResult } from '../dtos/index';
import { TruckSearchTable } from '../views/index';
import { Truck } from '../dtos/truck';
import {
	LOCATION_PARSER,
	LOCATION_PARSER_WITHOUT_ZIP,
	POPULATE_DESTINATION,
	POPULATE_ORIGIN,
} from '../../utils/location/index';
import { GET_CARRIER_BUSINESS_NAME } from '../../carrier/utils/get-carrier-business-name';
import { IS_EMPTY, IS_NOT_EMPTY } from '../../utils/stringUtils/string-utils';
import { IS_NULL_OR_UNDEFINED } from '../../utils/commonUtils/validation-utils';
import { DATE_MDY } from '../../utils/date/date-parser';
import { Carrier, FORMAT_SCORE_PERCENTAGE } from '../../carrier';
import { FORMAT_LOCATION } from '../../utils/location';

const DEFAULT_SORT_PROPERTY = 'frequency';

export const SEARCH_TRUCK_TABLE = (
	truckSearchResult: Array<TruckSearchResult> = [],
	searchedDestinationState: string
): Array<TruckSearchTable> => {
	let searchResults: Array<TruckSearchTable> = [];
	try {
		if (truckSearchResult.length > 0) {
			truckSearchResult.map((response: TruckSearchResult) => {
				if (response.trucks != undefined && response.trucks != null && response.trucks.length > 0) {
					response.trucks.map((searchedTruck: Truck) => {
						let truck = {} as TruckSearchTable;
						const carrier: Carrier = response.carrier;

						truck.businessName = GET_CARRIER_BUSINESS_NAME(
							carrier.businessName,
							carrier.doingBusinessAs
						);
						truck.oldCarrierId =
							carrier.carrierId != undefined && carrier.carrierId != null
								? carrier.carrierId
								: null;
						truck.carrierId = carrier.carrierHubId;
						truck.customerCarrier = carrier.customerCarrier;
						truck.originDeadhead = searchedTruck.originDeadhead;
						truck.firmDot = carrier.firmDot || null;
						truck.firmMc = carrier.firmMc || null;
						truck.customerCode = carrier.customerCode;
						truck.carrierContactsCount = carrier.contactCount;
						truck.lanesCount = carrier.lanesCount;
						truck.projectCount = carrier.projectCount;

						truck.location = LOCATION_PARSER(searchedTruck.loadStops[0].address);
						if (
							LOCATION_PARSER(searchedTruck.loadStops[searchedTruck.loadStops.length - 1].address)
								.length == 0
						) {
							if (
								searchedTruck.destinationStates.some(
									(state) => state?.toLowerCase() == searchedDestinationState?.toLowerCase()
								)
							) {
								truck.location +=
									' → ' +
									searchedTruck.destinationStates.find(
										(x) => x?.toLowerCase() === searchedDestinationState?.toLowerCase()
									);
							}
						} else {
							truck.location +=
								' → ' +
								LOCATION_PARSER(
									searchedTruck.loadStops[searchedTruck.loadStops.length - 1].address
								);
						}

						truck.carrierHQ = FORMAT_LOCATION(carrier.city, carrier.state, null);
						truck.match = searchedTruck.rating.score;

						if (response.isLane != undefined && response.isLane != null && response.isLane) {
							truck.isLane = true;
							truck.lanePath =
								LOCATION_PARSER_WITHOUT_ZIP(searchedTruck.loadStops[0].address) + ' → ';
							if (
								LOCATION_PARSER_WITHOUT_ZIP(
									searchedTruck.loadStops[searchedTruck.loadStops.length - 1].address
								).length == 0
							) {
								if (
									searchedTruck.destinationStates.some(
										(state) => state?.toLowerCase() == searchedDestinationState?.toLowerCase()
									)
								) {
									truck.lanePath += searchedTruck.destinationStates.find(
										(x) => x?.toLowerCase() === searchedDestinationState?.toLowerCase()
									);
								}
							} else {
								truck.lanePath += LOCATION_PARSER_WITHOUT_ZIP(
									searchedTruck.loadStops[searchedTruck.loadStops.length - 1].address
								);
							}
						} else {
							truck.availableDate = searchedTruck.availableDate;
							truck.isLane = false;
						}
						truck.drivers = carrier.drivers;
						truck.carrierLastFmcsaUpdate = carrier.carrierLastFmcsaUpdate;
						truck.powerUnits = carrier.powerUnits;
						truck.phone = carrier.phone;
						truck.fax = carrier.fax;
						truck.cellPhone = carrier.cellPhone;
						truck.businessEmailAddress = carrier.businessEmailAddress;
						truck.representative1 = carrier.representative1;
						truck.representative2 = carrier.representative2;
						truck.city = carrier.city;
						truck.state = carrier.state;
						truck.address1 = carrier.address1;
						truck.postcode = carrier.postcode;

						if (carrier.brokerExclusivity != undefined && carrier.brokerExclusivity != null) {
							truck.brokerExclusivity = carrier.brokerExclusivity;
						} else {
							truck.brokerExclusivity = null;
						}
						truck.frequency = getFrequency(searchedTruck);
						truck.destinationDeadhead = searchedTruck.destinationDeadhead;
						POPULATE_ORIGIN(truck, searchedTruck.loadStops[0].address);
						POPULATE_DESTINATION(truck, searchedTruck.loadStops[1].address);
						// set origin, destination, empty location is not supported by feedback
						if (IS_EMPTY(truck.originCity)) {
							if (IS_NOT_EMPTY(carrier.city)) {
								truck.originCity = carrier.city;
							} else if (IS_NOT_EMPTY(truck.originState)) {
								truck.originCity = truck.originState;
							}
						}
						if (IS_EMPTY(truck.originState)) {
							if (IS_NOT_EMPTY(carrier.state)) {
								truck.originState = carrier.state;
							}
						}
						if (IS_EMPTY(truck.destCity)) {
							truck.destCity = searchedDestinationState;
						}
						if (IS_EMPTY(truck.destState)) {
							truck.destState = searchedDestinationState;
						}
						truck.trailerTypes = searchedTruck.trailerType;
						truck.id = searchedTruck.truckId;
						truck.isTruckAndLane = false;
						truck.showAvailableDate = false;
						truck.searchId = truck.isLane ? response.truckLaneSearchId : response.truckSearchId;
						truck.notesCount = carrier.notesCount;
						truck.laneIds = response.laneIds;
						truck.hazmat = carrier.hazmat;
						truck.hasTeams = carrier.hasTeams;
						truck.hasTeamsUpdatedOn = carrier.hasTeamsUpdatedOn;
						truck.active = carrier.active;

						truck.carrierOnboardedOn = carrier.carrierOnboardedOn;
						truck.vettingSource = carrier.vettingSource;
						truck.authorityGrantDate = carrier.authorityGrantDate
							? DATE_MDY(carrier.authorityGrantDate)
							: 'N/A';
						truck.bipdInsuranceAmount = carrier.bipdInsuranceAmount;
						truck.trailers = carrier.trailers;
						truck.cargoInsuranceAmount = carrier.cargoInsuranceAmount;

						if (carrier.accountCarrierMap) {
							const accountCarrierMap = carrier.accountCarrierMap;
							truck.ccBookingRateDisplay = FORMAT_SCORE_PERCENTAGE(accountCarrierMap.ccBookingRate);
							truck.ccEngagementRateDisplay = FORMAT_SCORE_PERCENTAGE(
								accountCarrierMap.ccEngagementRate
							);
							truck.ccFalloffRateDisplay = FORMAT_SCORE_PERCENTAGE(accountCarrierMap.ccFalloffRate);
							truck.ccTotalBookedLoads = accountCarrierMap.ccTotalBookedLoads;
							truck.ccScore = accountCarrierMap.ccScore;
							truck.vettingAuthorityDisplay = accountCarrierMap.vettingAuthorityDisplay;
							truck.vettingInsuranceDisplay = accountCarrierMap.vettingInsuranceDisplay;
							truck.vettingOperationsDisplay = accountCarrierMap.vettingOperationsDisplay;
							truck.vettingSafetyDisplay = accountCarrierMap.vettingSafetyDisplay;
							truck.vettingOtherDisplay = accountCarrierMap.vettingOtherDisplay;
							truck.vettingOverallDisplay = accountCarrierMap.vettingOverallDisplay;
							truck.infractionDetails = accountCarrierMap.infractionDetails;
							truck.lastVettingTime = accountCarrierMap.lastVettingTime;
							truck.vettingSourceId = accountCarrierMap.vettingSourceId;
						}
						if (
							IS_EMPTY(truck.vettingOverallDisplay) &&
							IS_NOT_EMPTY(carrier.vettingOverallDisplay)
						) {
							truck.ccBookingRateDisplay = FORMAT_SCORE_PERCENTAGE(carrier.ccBookingRate);
							truck.ccEngagementRateDisplay = FORMAT_SCORE_PERCENTAGE(carrier.ccEngagementRate);
							truck.ccFalloffRateDisplay = FORMAT_SCORE_PERCENTAGE(carrier.ccFalloffRate);
							truck.ccTotalBookedLoads = carrier.ccTotalBookedLoads;
							// Only use global carrier ccScore if account-specific score wasn't already set
							if (IS_NULL_OR_UNDEFINED(truck.ccScore)) {
								truck.ccScore = carrier.ccScore;
							}
							truck.vettingAuthorityDisplay = carrier.vettingAuthorityDisplay;
							truck.vettingInsuranceDisplay = carrier.vettingInsuranceDisplay;
							truck.vettingOperationsDisplay = carrier.vettingOperationsDisplay;
							truck.vettingSafetyDisplay = carrier.vettingSafetyDisplay;
							truck.vettingOtherDisplay = carrier.vettingOtherDisplay;
							truck.vettingOverallDisplay = carrier.vettingOverallDisplay;
							truck.infractionDetails = carrier.infractionDetails;
							truck.lastVettingTime = carrier.lastVettingTime;
						}
						truck.haulPayApproved = carrier.haulPayApproved;
						truck.haulPayApprovedUpdatedAt = carrier.haulPayApprovedUpdatedAt;
						truck.recentFeedbackDate = searchedTruck.recentFeedbackDate;
						truck.rate = searchedTruck.rate;
						truck.rateUpdatedAt = searchedTruck.rateUpdatedAt;
						truck.ratePickupDate = searchedTruck.ratePickupDate;

						searchResults.push(truck);
					});
				}
			});

			searchResults = [...combineTruckAndLane(searchResults)];

			searchResults = [...sortSearchList(searchResults)];
		}
	} catch (error) {
		console.log('SEARCH_TRUCK_TABLE error: ', error);
	}

	return searchResults;
};

const sortSearchList = (list: Array<TruckSearchTable>): Array<TruckSearchTable> => {
	if (DEFAULT_SORT_PROPERTY == 'frequency') {
		let frequencyDaily = [],
			frequentDays = [],
			frequencyNumber = [],
			result: Array<TruckSearchTable> = [];
		list.map((search, index) => {
			if (search.frequency == 'Daily') frequencyDaily.push(index);
			else if (!/\D/.test(search.frequency)) frequencyNumber.push(index);
			else frequentDays.push(index);
		});
		frequencyDaily.map((searchIndex) => result.push(list[searchIndex]));
		frequentDays.map((searchIndex) => result.push(list[searchIndex]));
		frequencyNumber
			.sort((a, b) => 0 - (Number(list[a].frequency) > Number(list[b].frequency) ? 1 : -1))
			.map((searchIndex) => result.push(list[searchIndex]));
		return result;
	}
	return list.sort((left, right): number => {
		if (left[DEFAULT_SORT_PROPERTY] > right[DEFAULT_SORT_PROPERTY]) return -1;
		if (left[DEFAULT_SORT_PROPERTY] < right[DEFAULT_SORT_PROPERTY]) return 1;
		return 0;
	});
};

const getFrequency = (searchedTruck: Truck): string => {
	let result = '';
	if (
		searchedTruck.runDaily != undefined &&
		searchedTruck.runDaily != null &&
		searchedTruck.runDaily
	) {
		result = 'Daily';
	} else if (
		searchedTruck.monday ||
		searchedTruck.tuesday ||
		searchedTruck.wednesday ||
		searchedTruck.thursday ||
		searchedTruck.friday ||
		searchedTruck.saturday ||
		searchedTruck.sunday
	) {
		let daysOfWeek = [];
		if (searchedTruck.monday) {
			daysOfWeek.push('M');
		}
		if (searchedTruck.tuesday) {
			daysOfWeek.push('T');
		}
		if (searchedTruck.wednesday) {
			daysOfWeek.push('W');
		}
		if (searchedTruck.thursday) {
			daysOfWeek.push('TH');
		}
		if (searchedTruck.friday) {
			daysOfWeek.push('F');
		}
		if (searchedTruck.saturday) {
			daysOfWeek.push('SAT');
		}
		if (searchedTruck.sunday) {
			daysOfWeek.push('SUN');
		}
		if (daysOfWeek.length > 0) {
			result = daysOfWeek.join(', ');
		}
	} else if (searchedTruck.weeklyFrequency != undefined && searchedTruck.weeklyFrequency != null) {
		result = searchedTruck.weeklyFrequency.toString();
	}

	return result;
};

const combineTruckAndLane = (searchList: Array<TruckSearchTable>): Array<TruckSearchTable> => {
	const result: Array<TruckSearchTable> = searchList.reduce(
		(acc: Array<TruckSearchTable> = [], current) => {
			const x = acc.find((item) => item.carrierId === current.carrierId);
			if (!x) {
				return acc.concat([current]);
			} else {
				let ind = null;
				acc.map((search, index) => {
					if (ind == null && search.carrierId == current.carrierId) ind = index;
				});
				if (ind != null) {
					if (acc[ind].isLane && !current.isLane) {
						acc[ind].isTruckAndLane = true;
					}
					if (acc[ind].match < current.match) {
						acc[ind].originDeadhead = current.originDeadhead;
						acc[ind].destinationDeadhead = current.destinationDeadhead;
						acc[ind].availableDate = current.availableDate;
						acc[ind].match = current.match;
						acc[ind].location = current.location;
						acc[ind].lanePath = current.lanePath;
						acc[ind].carrierHQ = current.carrierHQ;
						acc[ind].originCity = current.originCity;
						acc[ind].originState = current.originState;
						acc[ind].destCity = current.destCity;
						acc[ind].destState = current.destState;
						acc[ind].trailerTypes = current.trailerTypes;
						acc[ind].id = current.id;
					}
				}
				return acc;
			}
		},
		[]
	);
	return result;
};
